import type { PracticeArea } from '../intake';
import { renderTemplate } from '../templates';

/**
 * Matter types, configurable status stages, and the rules that turn a stage change into a client
 * update (case status updates).
 */

export type Stage = {
  key: string;
  label: string;
  /** Send the client an update when a matter enters this stage. */
  notifyClient: boolean;
  /** Update template to use (update_templates.key); defaults to `stage_<key>` then `stage_generic`. */
  templateKey?: string;
};

export type MatterTypeDefinition = { key: string; name: string; practiceArea: PracticeArea; stages: Stage[] };

const s = (key: string, label: string, notifyClient = true): Stage => ({ key, label, notifyClient });

export const DEFAULT_MATTER_TYPES: Record<PracticeArea, MatterTypeDefinition> = {
  family: {
    key: 'family',
    name: 'Family law',
    practiceArea: 'family',
    stages: [s('opened', 'File opened'), s('disclosure', 'Financial disclosure'), s('negotiation', 'Negotiation'), s('court', 'Court proceedings'), s('agreement', 'Agreement or order'), s('closed', 'Closed', false)],
  },
  personal_injury: {
    key: 'personal_injury',
    name: 'Personal injury',
    practiceArea: 'personal_injury',
    stages: [s('opened', 'File opened'), s('investigation', 'Investigation and records'), s('claim', 'Claim filed'), s('discovery', 'Discovery'), s('settlement', 'Settlement discussions'), s('resolved', 'Resolved'), s('closed', 'Closed', false)],
  },
  real_estate: {
    key: 'real_estate',
    name: 'Real estate',
    practiceArea: 'real_estate',
    stages: [s('opened', 'File opened'), s('title_search', 'Title search'), s('documents', 'Closing documents'), s('closing', 'Closing day'), s('closed', 'Closed', false)],
  },
  wills_estates: {
    key: 'wills_estates',
    name: 'Wills and estates',
    practiceArea: 'wills_estates',
    stages: [s('opened', 'File opened'), s('drafting', 'Drafting'), s('review', 'Client review'), s('signing', 'Signing'), s('closed', 'Closed', false)],
  },
  immigration: {
    key: 'immigration',
    name: 'Immigration',
    practiceArea: 'immigration',
    stages: [s('opened', 'File opened'), s('documents', 'Gathering documents'), s('submitted', 'Application submitted'), s('decision', 'Decision received'), s('closed', 'Closed', false)],
  },
  corporate: {
    key: 'corporate',
    name: 'Corporate and business',
    practiceArea: 'corporate',
    stages: [s('opened', 'File opened'), s('drafting', 'Drafting'), s('negotiation', 'Negotiation'), s('signing', 'Signing'), s('closed', 'Closed', false)],
  },
};

export function stageLabel(stages: Stage[], key: string | null | undefined): string {
  return stages.find((st) => st.key === key)?.label ?? key ?? '';
}

export function validateStages(stages: Stage[]): string[] {
  const problems: string[] = [];
  if (stages.length === 0) problems.push('A matter type needs at least one stage.');
  const keys = new Set<string>();
  for (const st of stages) {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(st.key)) problems.push(`Stage key "${st.key}" must be lowercase letters, digits and underscores.`);
    if (keys.has(st.key)) problems.push(`Duplicate stage "${st.key}".`);
    keys.add(st.key);
    if (!st.label?.trim()) problems.push(`Stage "${st.key}" needs a label.`);
  }
  return problems;
}

export type UpdateTemplate = { key: string; subject: string; body: string; smsBody?: string | null };

export type UpdateMode = 'auto' | 'approve';

export type PlannedUpdate = {
  channel: 'email' | 'sms';
  subject: string | null;
  body: string;
  /** 'sent' = send now (firm-approved template), 'pending_approval' = a lawyer approves first. */
  status: 'sent' | 'pending_approval';
  templateKey: string;
  stageKey: string;
};

export type StageChangeInput = {
  stages: Stage[];
  fromStage: string | null;
  toStage: string;
  templates: UpdateTemplate[];
  vars: Record<string, string>;
  mode: UpdateMode;
  client: { email: string | null; phone: string | null; smsOptIn: boolean };
  /** Plan includes sms_updates. */
  smsAllowed: boolean;
};

/**
 * The client updates a stage change should produce. Nothing when the stage did not change, the
 * new stage is not client-facing, or the client cannot be reached. Moving backwards never notifies
 * (corrections should not alarm clients). In `approve` mode updates wait for a lawyer.
 */
export function updatesForStageChange(input: StageChangeInput): PlannedUpdate[] {
  const { stages, fromStage, toStage } = input;
  if (fromStage === toStage) return [];
  const to = stages.find((st) => st.key === toStage);
  if (!to || !to.notifyClient) return [];
  const fromIdx = stages.findIndex((st) => st.key === fromStage);
  const toIdx = stages.indexOf(to);
  if (fromIdx >= 0 && toIdx < fromIdx) return [];
  const template =
    input.templates.find((t) => t.key === to.templateKey) ??
    input.templates.find((t) => t.key === `stage_${to.key}`) ??
    input.templates.find((t) => t.key === 'stage_generic');
  if (!template) return [];
  const vars: Record<string, string> = { ...input.vars, stage: to.label };
  const status = input.mode === 'auto' ? 'sent' : 'pending_approval';
  const out: PlannedUpdate[] = [];
  if (input.client.email) {
    out.push({ channel: 'email', subject: renderTemplate(template.subject, vars), body: renderTemplate(template.body, vars), status, templateKey: template.key, stageKey: to.key });
  }
  if (input.smsAllowed && input.client.phone && input.client.smsOptIn) {
    const smsText = template.smsBody?.trim() ? template.smsBody : `${vars.firm_name ?? 'Your law firm'}: your matter is now at "${to.label}". Check your email or portal for details.`;
    out.push({ channel: 'sms', subject: null, body: renderTemplate(smsText, vars), status, templateKey: template.key, stageKey: to.key });
  }
  return out;
}

export const DEFAULT_UPDATE_TEMPLATES: (UpdateTemplate & { name: string })[] = [
  {
    key: 'stage_generic',
    name: 'Stage update (any stage)',
    subject: 'Update on your matter with {{firm_name}}',
    body: 'Hello {{client_first_name}},\n\nYour matter "{{matter_title}}" has moved to the stage: {{stage}}.\n\nYour lawyer, {{lawyer_name}}, will be in touch if anything is needed from you. You can reply to this email with questions.\n\n{{firm_name}}',
    smsBody: '{{firm_name}}: your matter is now at "{{stage}}". Details by email.',
  },
  {
    key: 'stage_opened',
    name: 'File opened',
    subject: 'Welcome to {{firm_name}}',
    body: 'Hello {{client_first_name}},\n\nThank you for choosing {{firm_name}}. We have opened your file "{{matter_title}}" (file {{matter_number}}). {{lawyer_name}} is your lawyer.\n\nWe will keep you updated as your matter progresses.\n\n{{firm_name}}',
    smsBody: '{{firm_name}}: your file {{matter_number}} is open. Watch your email for next steps.',
  },
  {
    key: 'intake_received',
    name: 'Intake received (auto-reply)',
    subject: 'We received your information',
    body: 'Hello {{client_first_name}},\n\nThank you for contacting {{firm_name}}. We received your intake form and a member of our team will review it shortly.\n\nPlease note: submitting this form does not create a lawyer-client relationship, and nothing in this message is legal advice.\n\n{{firm_name}}',
  },
  {
    key: 'intake_missing_info',
    name: 'Missing information request',
    subject: 'A few more details for {{firm_name}}',
    body: 'Hello {{client_first_name}},\n\nThank you for your intake. To prepare for your consultation we still need: {{missing_list}}.\n\nYou can add them here: {{followup_link}}\n\n{{firm_name}}',
  },
  // Resolution wording (from the NorthOps legal pack's "settlement or resolution" status stage).
  // The pack's other stages are already covered: intake complete → intake_received, consult
  // scheduled → the booking confirmation email, retainer signed → stage_opened, case stage
  // updated → stage_generic. Keys follow `stage_<stage key>` so updatesForStageChange picks them
  // for the default personal injury ("resolved") and family law ("agreement") stages. Existing
  // workspaces get them from migration 0004 (keep the wording there in sync).
  {
    key: 'stage_resolved',
    name: 'Matter resolved',
    subject: 'Your matter with {{firm_name}} has been resolved',
    body: 'Hello {{client_first_name}},\n\nYour matter "{{matter_title}}" has reached a resolution. {{lawyer_name}} will be in touch with the next steps, including anything still needed from you before we close your file.\n\nYou can reply to this email with questions.\n\n{{firm_name}}',
    smsBody: '{{firm_name}}: your matter has reached a resolution. Your lawyer will follow up with next steps by email.',
  },
  {
    key: 'stage_agreement',
    name: 'Agreement or order',
    subject: 'Update on your matter with {{firm_name}}',
    body: 'Hello {{client_first_name}},\n\nYour matter "{{matter_title}}" has moved to the stage: {{stage}}. {{lawyer_name}} will be in touch to go over the details and the next steps with you.\n\nYou can reply to this email with questions.\n\n{{firm_name}}',
    smsBody: '{{firm_name}}: there is an update on your matter ("{{stage}}"). Your lawyer will follow up with details by email.',
  },
];
